import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { fmtDateTime } from "@/lib/format";
import { FileSpreadsheet, Plus, Trash2, Save, Loader2, X } from "lucide-react";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

function Grid({ report, readOnly, onChange }) {
  const cols = report.columns || [];
  const rows = report.rows || [];
  const setCols = (c) => onChange({ ...report, columns: c });
  const setRows = (r) => onChange({ ...report, rows: r });

  return (
    <div className="overflow-x-auto rounded-xl border border-emerald-500/15" data-testid="report-grid">
      <table className="w-full border-collapse text-sm min-w-[560px]">
        <thead>
          <tr className="bg-white/5">
            <th className="w-10 px-2 py-2 text-xs text-muted-foreground font-normal">#</th>
            {cols.map((c, ci) => (
              <th key={c.id} className="px-2 py-2 text-left min-w-[140px]">
                <div className="flex items-center gap-1">
                  {readOnly ? (
                    <span className="font-semibold">{c.name}</span>
                  ) : (
                    <Input
                      value={c.name}
                      onChange={(e) => { const n = [...cols]; n[ci] = { ...c, name: e.target.value }; setCols(n); }}
                      className="h-8 bg-[#0A0D0C] border-emerald-500/20 font-semibold"
                      data-testid={`col-name-${ci}`}
                    />
                  )}
                  {!readOnly && cols.length > 1 && (
                    <button onClick={() => setCols(cols.filter((_, i) => i !== ci))} className="text-muted-foreground hover:text-red-400" title="Delete column" data-testid={`col-del-${ci}`}><X className="w-4 h-4" /></button>
                  )}
                </div>
              </th>
            ))}
            {!readOnly && (
              <th className="w-10 px-2 py-2"><Button size="sm" variant="ghost" onClick={() => setCols([...cols, { id: uid(), name: "New column" }])} className="h-8 w-8 p-0 text-emerald-400" title="Add column" data-testid="add-column-btn"><Plus className="w-4 h-4" /></Button></th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={r.id} className="border-t border-white/5" data-testid={`report-row-${ri}`}>
              <td className="px-2 py-1 text-xs text-muted-foreground text-center">{ri + 1}</td>
              {cols.map((c) => (
                <td key={c.id} className="px-1 py-1">
                  {readOnly ? (
                    <span className="block px-2 py-1">{r.cells?.[c.id] || "—"}</span>
                  ) : (
                    <Input
                      value={r.cells?.[c.id] || ""}
                      onChange={(e) => { const n = [...rows]; n[ri] = { ...r, cells: { ...r.cells, [c.id]: e.target.value } }; setRows(n); }}
                      className="h-8 bg-transparent border-transparent hover:border-emerald-500/20 focus:border-emerald-500/40"
                      data-testid={`cell-${ri}-${c.id}`}
                    />
                  )}
                </td>
              ))}
              {!readOnly && (
                <td className="px-2 py-1"><button onClick={() => setRows(rows.filter((_, i) => i !== ri))} className="text-muted-foreground hover:text-red-400" title="Delete row" data-testid={`row-del-${ri}`}><Trash2 className="w-4 h-4" /></button></td>
              )}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={cols.length + 2} className="px-4 py-6 text-center text-muted-foreground text-sm">No rows yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const isMgr = ["manager", "admin", "hr", "super_admin"].includes(user?.role);
  const [view, setView] = useState("mine");
  const [reports, setReports] = useState([]);
  const [team, setTeam] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadMine = async () => { setLoading(true); try { setReports((await api.get("/reports")).data); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } setLoading(false); };
  const loadTeam = async () => { setLoading(true); try { setTeam((await api.get("/reports/team")).data); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } setLoading(false); };

  useEffect(() => { if (view === "mine") loadMine(); else loadTeam(); }, [view]); // eslint-disable-line

  const createReport = async () => {
    try { const { data } = await api.post("/reports", { title: "Untitled report" }); await loadMine(); openReport(data, false); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openReport = (r, readOnly) => { setSelected({ ...r, readOnly }); setDraft(JSON.parse(JSON.stringify(r))); };
  const closeReport = () => { setSelected(null); setDraft(null); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/reports/${draft.id}`, { title: draft.title, columns: draft.columns, rows: draft.rows });
      toast.success("Report saved");
      setDraft(JSON.parse(JSON.stringify(data)));
      setSelected({ ...data, readOnly: false });
      loadMine();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/reports/${r.id}`); toast.success("Report deleted"); if (selected?.id === r.id) closeReport(); loadMine(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // ----- Detail editor -----
  if (selected) {
    const readOnly = selected.readOnly;
    return (
      <div className="space-y-5" data-testid="report-editor">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" onClick={closeReport} data-testid="report-back-btn">← Back</Button>
          {readOnly ? (
            <h1 className="font-display text-xl font-bold truncate">{draft.title}</h1>
          ) : (
            <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="max-w-xs bg-[#0A0D0C] border-emerald-500/20 font-display text-lg font-bold" data-testid="report-title-input" />
          )}
          {readOnly && <Badge className="bg-sky-500/20 text-sky-300">Read only · {draft.owner_name}</Badge>}
          <div className="ml-auto flex items-center gap-2">
            {!readOnly && <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="report-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}</Button>}
          </div>
        </div>

        <Grid report={draft} readOnly={readOnly} onChange={setDraft} />

        {!readOnly && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setDraft({ ...draft, rows: [...(draft.rows || []), { id: uid(), cells: {} }] })} className="border-emerald-500/30" data-testid="add-row-btn"><Plus className="w-4 h-4 mr-1" /> Add row</Button>
            <Button variant="outline" onClick={() => setDraft({ ...draft, columns: [...(draft.columns || []), { id: uid(), name: "New column" }] })} className="border-emerald-500/30" data-testid="add-column-footer-btn"><Plus className="w-4 h-4 mr-1" /> Add column</Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground">Tip: rename any column header and add as many rows or columns as you need — it works like a spreadsheet. Remember to Save.</p>
      </div>
    );
  }

  // ----- List view -----
  const list = view === "mine" ? reports : team;
  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-emerald-400" /><h1 className="font-display text-2xl font-bold">KPI &amp; Reports</h1></div>
        {view === "mine" && <Button onClick={createReport} className="ml-auto bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="new-report-btn"><Plus className="w-4 h-4 mr-1" /> New report</Button>}
      </div>

      {isMgr && (
        <div className="flex gap-2">
          <Button variant={view === "mine" ? "default" : "outline"} onClick={() => setView("mine")} className={view === "mine" ? "bg-emerald-500 text-[#062217]" : "border-emerald-500/20"} data-testid="tab-my-reports">My reports</Button>
          <Button variant={view === "team" ? "default" : "outline"} onClick={() => setView("team")} className={view === "team" ? "bg-emerald-500 text-[#062217]" : "border-emerald-500/20"} data-testid="tab-team-reports">Team reports</Button>
        </div>
      )}

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-emerald-500" /></div>
      ) : list.length === 0 ? (
        <div className="card-emerald p-10 text-center text-muted-foreground" data-testid="reports-empty">
          {view === "mine" ? "You have no reports yet. Create one to start logging your KPIs." : "No team reports have been created yet."}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((r) => (
            <div key={r.id} className="card-emerald p-5 cursor-pointer hover:border-emerald-500/40 transition-colors" onClick={() => openReport(r, view === "team")} data-testid="report-card">
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium truncate">{r.title}</p>
                {view === "mine" && <button onClick={(e) => { e.stopPropagation(); remove(r); }} className="text-muted-foreground hover:text-red-400 shrink-0" data-testid="report-delete-btn"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">{(r.rows || []).length} rows · {(r.columns || []).length} columns</p>
              {view === "team" && <p className="text-xs text-emerald-300 mt-1">{r.owner_name}</p>}
              <p className="text-[11px] text-muted-foreground mt-2">Updated {fmtDateTime(r.updated_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
